import type { ManualChapter } from './manual';

/**
 * Dine-in tables user manual — the till's table actions on HELD orders.
 *
 * Written against `pos-api/src/pos/held-orders/tableActions.*` and the cashier's
 * `TablePickerModal` / `SplitTransferModal`. Shared: the admin manual shows
 * it at /manual/tables, and the till opens it in a pop-up from the cart ("How
 * tables work") — the reader is usually a cashier at the till mid-shift.
 *
 * Things most likely to be misunderstood, stated explicitly below:
 *   1. every action needs the order HELD on a table first;
 *   2. "Split every item" splits per item LINE, not per piece;
 *   3. an absorbed order is cancelled with its money zeroed, never deleted;
 *   4. a permission grant needs a fresh sign-in.
 */
export const tablesManualChapter: ManualChapter = {
  id: 'tables',
  icon: 'M3 10h18M5 10v9M19 10v9M8 10V6h8v4',
  title: { en: 'Dine-in tables', ar: 'طاولات الصالة' },
  summary: {
    en: 'Split a table’s bill, split every item into its own bill, transfer items, change, move and merge tables, and assign a waiter.',
    ar: 'فصل فاتورة الطاولة، وتقسيم كل صنف في فاتورة، ونقل الأصناف، وتغيير الطاولات ونقلها ودمجها، وتعيين النادل.',
  },
  audience: {
    en: 'For cashiers and supervisors in a restaurant, and for the owner who grants the permissions.',
    ar: 'للكاشير والمشرفين في المطعم، وللمالك الذي يمنح الصلاحيات.',
  },
  sections: [
    // ── 1. Overview ─────────────────────────────────────────────────────
    {
      id: 'overview',
      title: { en: 'What the table actions are', ar: 'ما هي إجراءات الطاولات' },
      blocks: [
        {
          kind: 'para',
          en: 'Table actions work on HELD dine-in orders: the order is parked on its table with Hold, then split, moved or merged before it is paid. No stock moves, and the money always adds back up to the fils.',
          ar: 'تعمل إجراءات الطاولات على الطلبات المعلّقة في الصالة: الطلب يُحفظ على الطاولة بزر تعليق، ثم يُفصل أو يُنقل أو يُدمج قبل الدفع. لا تحدث أي حركة مخزون، ومجموع المبالغ يبقى كما هو حتى آخر فلس.',
        },
        {
          kind: 'fields',
          rows: [
            {
              label: { en: 'Separate', ar: 'فصل' },
              body: {
                en: 'Chosen items move to a new bill on the same table. Permission: Separate orders on dine-in.',
                ar: 'أصناف مختارة تنتقل إلى فاتورة جديدة على نفس الطاولة. الصلاحية: Separate orders on dine-in.',
              },
            },
            {
              label: { en: 'Split every item', ar: 'تقسيم كل صنف في فاتورة' },
              body: {
                en: 'Every item becomes its own bill, with a new number, on the same table. Permission: Separate orders on dine-in.',
                ar: 'كل صنف يصبح فاتورة مستقلة برقم جديد على نفس الطاولة. الصلاحية: Separate orders on dine-in.',
              },
            },
            {
              label: { en: 'Transfer items', ar: 'نقل الأصناف' },
              body: {
                en: 'Chosen items move to another table’s order. Permission: Transfer items between Dine-In tables.',
                ar: 'أصناف مختارة تنتقل إلى طلب طاولة أخرى. الصلاحية: Transfer items between Dine-In tables.',
              },
            },
            {
              label: { en: 'Change', ar: 'تغيير' },
              body: {
                en: 'The whole order moves to a free table. Permission: Change tables on dine-in.',
                ar: 'نقل الطلب كاملاً إلى طاولة متاحة. الصلاحية: Change tables on dine-in.',
              },
            },
            {
              label: { en: 'Move', ar: 'نقل (الطاولة)' },
              body: {
                en: 'The order moves to any table; an order already there is merged in. Permission: Move tables on dine-in.',
                ar: 'نقل الطلب إلى أي طاولة؛ إن كان عليها طلب يُدمج معه. الصلاحية: Move tables on dine-in.',
              },
            },
            {
              label: { en: 'Merge', ar: 'دمج' },
              body: {
                en: 'Several tables and their orders are joined into one order. Permission: Join tables on dine-in.',
                ar: 'ضم عدة طاولات وطلباتها في طلب واحد. الصلاحية: Join tables on dine-in.',
              },
            },
            {
              label: { en: 'Waiter', ar: 'النادل' },
              body: {
                en: 'Assigns a waiter to the order; the name shows on the table. Permission: Change Salesman.',
                ar: 'تعيين نادل للطلب، ويظهر اسمه على الطاولة. الصلاحية: Change Salesman.',
              },
            },
          ],
        },
        {
          kind: 'note',
          en: 'The user also needs Holded Orders to open held orders. After any permission is granted, the cashier must sign out and back in.',
          ar: 'يحتاج المستخدم أيضاً صلاحية Holded Orders لفتح الطلبات المعلّقة. بعد منح أي صلاحية يجب على الكاشير تسجيل الخروج والدخول من جديد.',
        },
      ],
    },

    // ── 2. Opening the dialog ───────────────────────────────────────────
    {
      id: 'open',
      title: { en: 'Opening a table order', ar: 'فتح طلب الطاولة' },
      blocks: [
        {
          kind: 'para',
          en: 'The “⇄ Separate / Transfer items” button appears only for a held order on a table, at the top of the cart: under the order-type, customer and table tiles, above the first item.',
          ar: 'زر «⇄ فصل / نقل الأصناف» يظهر فقط لطلب معلّق على طاولة، في أعلى السلة تحت بطاقات نوع الطلب والعميل والطاولة وفوق أول صنف.',
        },
        {
          kind: 'steps',
          en: [
            'Choose Dine-in, tap the table tile and pick a free table.',
            'Add the items and press Hold. The order goes to the kitchen and the table turns occupied.',
            'Later, open Tables, tap the occupied table, then its order. It loads back into the cart with the table tile showing its name.',
            'Press “⇄ Separate / Transfer items”. The dialog opens with Separate (a new bill on the same table) selected; Transfer (to another table) sits beside it.',
          ],
          ar: [
            'اختر صالة، ثم اضغط بطاقة الطاولة واختر طاولة متاحة.',
            'أضف الأصناف واضغط تعليق. يذهب الطلب للمطبخ وتصبح الطاولة مشغولة.',
            'لاحقاً افتح الطاولات، واضغط الطاولة المشغولة ثم طلبها. يعود الطلب إلى السلة وتظهر بطاقة الطاولة باسمها.',
            'اضغط «⇄ فصل / نقل الأصناف». تفتح النافذة وخيار فصل (فاتورة جديدة على نفس الطاولة) مختار، وبجانبه نقل (إلى طاولة أخرى).',
          ],
        },
        {
          kind: 'note',
          en: 'If you changed the cart after opening the order, the till asks you to save it (Hold) first. Change, Move, Merge and Waiter are at the top of the Pick a table window.',
          ar: 'إذا غيّرت السلة بعد فتح الطلب يطلب منك النظام حفظه (تعليق) أولاً. أزرار تغيير ونقل ودمج والنادل في أعلى نافذة اختر طاولة.',
        },
      ],
    },

    // ── 3. Separate + split every item ──────────────────────────────────
    {
      id: 'separate',
      title: { en: 'Separate and Split every item', ar: 'الفصل وتقسيم كل صنف في فاتورة' },
      blocks: [
        {
          kind: 'example',
          title: { en: 'Table 5, order #300', ar: 'طاولة 5، الطلب #300' },
          lines: [
            { en: 'Burger  x 2     5.000', ar: 'برجر   × 2     5.000' },
            { en: 'Fries   x 1     1.000', ar: 'بطاطا  × 1     1.000' },
            { en: 'Pepsi   x 2     1.000', ar: 'بيبسي  × 2     1.000' },
            { en: 'Total           7.000', ar: 'المجموع        7.000' },
          ],
        },
        {
          kind: 'para',
          en: 'Separate — one guest pays for one burger and one Pepsi:',
          ar: 'الفصل — أحد الضيوف يريد دفع برجر واحد وبيبسي واحد:',
        },
        {
          kind: 'steps',
          en: [
            'In Separate, tick Burger and type 1 in Move; do the same for Pepsi.',
            'Press Save.',
          ],
          ar: [
            'في وضع فصل ضع علامة على البرجر واكتب 1 في عمود ينقل، وكذلك البيبسي.',
            'اضغط حفظ.',
          ],
        },
        {
          kind: 'example',
          title: { en: 'Result', ar: 'النتيجة' },
          lines: [
            { en: '#300  Burger, Fries, Pepsi   4.000', ar: '#300  برجر، بطاطا، بيبسي   4.000' },
            { en: '#301  Burger, Pepsi (new)    3.000', ar: '#301  برجر، بيبسي (جديد)   3.000' },
            { en: 'Both on table 5, together    7.000', ar: 'كلاهما على طاولة 5، المجموع 7.000' },
          ],
        },
        {
          kind: 'para',
          en: 'Split every item — each guest pays for a different item:',
          ar: 'تقسيم كل صنف في فاتورة — كل ضيف يدفع صنفاً مختلفاً:',
        },
        {
          kind: 'steps',
          en: [
            'In Separate, leave “Print the bills” ticked (the default).',
            'Press “Split every item” and confirm.',
          ],
          ar: [
            'في وضع فصل اترك «طباعة الفواتير» مفعّلاً (الافتراضي).',
            'اضغط «تقسيم كل صنف في فاتورة» وأكّد.',
          ],
        },
        {
          kind: 'example',
          title: { en: 'Result on table 5', ar: 'النتيجة على طاولة 5' },
          lines: [
            { en: '#300 (original)  Burger x 2   5.000', ar: '#300 (الأصلية)  برجر × 2   5.000' },
            { en: '#301 (new)       Fries  x 1   1.000', ar: '#301 (جديدة)    بطاطا × 1  1.000' },
            { en: '#302 (new)       Pepsi  x 2   1.000', ar: '#302 (جديدة)    بيبسي × 2  1.000' },
          ],
        },
        {
          kind: 'bullets',
          en: [
            'The split is per item LINE, not per piece: Burger x 2 stays one bill. To split it, use a plain Separate with quantity 1.',
            'All the bills print at once, and each is paid separately from the table.',
            'The kitchen does not get the items again.',
            'An order with only one item is refused — there is nothing to split. Separate cannot take every item; at least one stays.',
          ],
          ar: [
            'التقسيم حسب سطر الصنف لا حسب القطعة: برجر × 2 يبقى في فاتورة واحدة. لتقسيمه استخدم الفصل العادي بكمية 1.',
            'تُطبع كل الفواتير مباشرة، وتُدفع كل واحدة بمفردها من الطاولة.',
            'المطبخ لا يستلم الأصناف مرة ثانية.',
            'طلب فيه صنف واحد فقط يُرفض تقسيمه. والفصل لا يأخذ كل الأصناف؛ يبقى صنف واحد على الأقل.',
          ],
        },
      ],
    },

    // ── 4. Transfer / change / move / merge ─────────────────────────────
    {
      id: 'transfer',
      title: { en: 'Transfer, Change, Move and Merge', ar: 'النقل والتغيير والدمج' },
      blocks: [
        {
          kind: 'para',
          en: 'Transfer items — a guest from table 5 moves to table 8 with the two Pepsis:',
          ar: 'نقل الأصناف — ضيف على طاولة 5 انتقل إلى طاولة 8 ومعه بيبسي × 2:',
        },
        {
          kind: 'steps',
          en: [
            'In the Separate / Transfer dialog choose Transfer and tick Pepsi with quantity 2.',
            'Under “To table” pick table 8 and press Save.',
          ],
          ar: [
            'في نافذة فصل / نقل اختر نقل، وضع علامة على البيبسي بكمية 2.',
            'في «إلى الطاولة» اختر طاولة 8 واضغط حفظ.',
          ],
        },
        {
          kind: 'note',
          en: 'If table 8 has an order the items join it; if it is free (“Free · new order”) a new order is opened there. Moving every item closes the original order.',
          ar: 'إن كانت طاولة 8 عليها طلب تُضاف الأصناف إليه، وإن كانت متاحة («متاحة · طلب جديد») يُنشأ لها طلب جديد. إذا نُقلت كل الأصناف يُغلق الطلب الأصلي.',
        },
        {
          kind: 'para',
          en: 'Change, Move and Merge are in the Pick a table window: open the held order, then tap the table tile.',
          ar: 'التغيير والنقل والدمج في نافذة اختر طاولة: افتح الطلب المعلّق ثم اضغط بطاقة الطاولة.',
        },
        {
          kind: 'fields',
          rows: [
            {
              label: { en: 'Change', ar: 'تغيير' },
              body: {
                en: 'Tap Change, then a free table. Example: table 5’s guests move to the window — the whole order goes to table 12.',
                ar: 'اضغط تغيير ثم طاولة متاحة. مثال: ضيوف طاولة 5 انتقلوا للنافذة — الطلب كله إلى طاولة 12.',
              },
            },
            {
              label: { en: 'Move', ar: 'نقل' },
              body: {
                en: 'Tap Move, then any table. Example: table 5 onto occupied table 3 — table 3’s order is merged in.',
                ar: 'اضغط نقل ثم أي طاولة. مثال: طاولة 5 إلى طاولة 3 المشغولة — طلب طاولة 3 يُدمج في الطلب.',
              },
            },
            {
              label: { en: 'Merge', ar: 'دمج' },
              body: {
                en: 'Tap Merge, the tables to join, then Merge below. Example: a party on tables 6 and 7 — one order holds both tables.',
                ar: 'اضغط دمج، ثم الطاولات المراد ضمها، ثم دمج بالأسفل. مثال: مجموعة على طاولتي 6 و 7 — طلب واحد يشغل الطاولتين.',
              },
            },
          ],
        },
        {
          kind: 'note',
          en: 'An order merged into another is cancelled with its money zeroed — never deleted — and the total does not change.',
          ar: 'الطلب الذي يُدمج في غيره يُلغى وتُصفّر مبالغه (لا يُحذف)، ومجموع الطلبات لا يتغير.',
        },
      ],
    },

    // ── 5. Waiter ───────────────────────────────────────────────────────
    {
      id: 'waiter',
      title: { en: 'Assigning a waiter', ar: 'تعيين النادل' },
      blocks: [
        {
          kind: 'steps',
          en: [
            'In Pick a table, tap the purple Waiter button.',
            'Tap the table (it must be occupied). If it holds several orders, pick one.',
            'Choose the waiter from the list.',
          ],
          ar: [
            'في نافذة اختر طاولة اضغط الزر البنفسجي النادل.',
            'اضغط الطاولة (يجب أن تكون مشغولة). إن كان عليها أكثر من طلب اختر الطلب.',
            'اختر النادل من القائمة.',
          ],
        },
        {
          kind: 'para',
          en: 'The waiter’s name shows on the table, and the paid invoice is credited to them as the salesman. The list is the same as Change Salesman: Cashier and Salesman role users linked to the branch. A free table gives a warning, because a waiter goes on an order.',
          ar: 'يظهر اسم النادل على الطاولة، وعند الدفع تُسجَّل الفاتورة باسمه كبائع. القائمة هي نفس قائمة تغيير البائع: مستخدمو دور الكاشير والبائع المرتبطون بالفرع. طاولة متاحة تعطي تنبيهاً لأن النادل يُعيَّن على الطلب.',
        },
      ],
    },

    // ── 6. Troubleshooting ──────────────────────────────────────────────
    {
      id: 'faq',
      title: { en: 'If a button is missing', ar: 'إذا لم يظهر الزر' },
      blocks: [
        {
          kind: 'faq',
          rows: [
            {
              q: { en: 'I can’t see “Separate / Transfer items”.', ar: 'لا أرى زر «فصل / نقل الأصناف».' },
              a: {
                en: 'The order must be HELD on a table and open in the cart — a new order not yet held has no button. The cart’s table tile must show the table name, not “No table”.',
                ar: 'يجب أن يكون الطلب معلّقاً على طاولة ومفتوحاً في السلة — الطلب الجديد قبل التعليق لا يظهر له الزر. وبطاقة الطاولة في السلة يجب أن تُظهر اسم الطاولة لا «لا توجد طاولة».',
              },
            },
            {
              q: { en: 'The permission was granted but nothing changed.', ar: 'تم منح الصلاحية ولم يتغير شيء.' },
              a: {
                en: 'Permissions are read at sign-in. Sign out of the till and back in.',
                ar: 'الصلاحيات تُقرأ عند تسجيل الدخول. سجّل الخروج من الكاشير ثم ادخل من جديد.',
              },
            },
            {
              q: { en: 'There is no table tile in the cart at all.', ar: 'لا توجد بطاقة طاولة في السلة إطلاقاً.' },
              a: {
                en: 'Restaurant mode (IsRestaurant) is off for the company. Turn it on in POS Settings.',
                ar: 'خيار المطعم (IsRestaurant) غير مفعّل للشركة. فعّله من إعدادات نقطة البيع.',
              },
            },
          ],
        },
      ],
    },
  ],
};
